import type { AlertProps, ButtonVariantProp, IconName, OverlayTriggerState } from '@meterup/atto';
import type { MouseEventHandler } from 'react';
import {
  Alert,
  Body,
  Button,
  colors,
  darkThemeSelector,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FieldContainer,
  PrimaryField,
  Text,
  TextInput,
} from '@meterup/atto';
import { useCallback, useMemo, useRef, useState } from 'react';

import { ucfirst } from '../../helpers/utils/strings';
import { keyframes, styled } from '../../stitches';

const destructiveDialogWarningWave = keyframes({
  '0%': {
    backgroundPosition: '200% 50%',
  },
  '100%': {
    backgroundPosition: '-200% 50%',
  },
});

const DestructiveDialogWarning = styled(Text, {
  fontWeight: '$bold',
  background: `linear-gradient(
    90deg,
    $$destructiveInitial 0%,
    $$destructiveStage 25%,
    $$destructiveInitial 50%,
    $$destructiveStage 75%,
    $$destructiveInitial 100%
  )`,
  backgroundSize: '200% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  animation: `${destructiveDialogWarningWave} 4s linear infinite`,

  variants: {
    variant: {
      alternative: {
        $$destructiveInitial: colors.blue500,
        $$destructiveStage: colors.blue800,

        [darkThemeSelector]: {
          $$destructiveInitial: colors.blue300,
          $$destructiveStage: colors.blue50,
        },
      },
      attention: {
        $$destructiveInitial: colors.amber500,
        $$destructiveStage: colors.amber800,

        [darkThemeSelector]: {
          $$destructiveInitial: colors.amber300,
          $$destructiveStage: colors.amber50,
        },
      },
      brand: {
        $$destructiveInitial: colors.brand500,
        $$destructiveStage: colors.brand800,

        [darkThemeSelector]: {
          $$destructiveInitial: colors.brand300,
          $$destructiveStage: colors.brand50,
        },
      },
      negative: {
        $$destructiveInitial: colors.red500,
        $$destructiveStage: colors.red800,

        [darkThemeSelector]: {
          $$destructiveInitial: colors.red300,
          $$destructiveStage: colors.red50,
        },
      },
      neutral: {
        $$destructiveInitial: colors.gray500,
        $$destructiveStage: colors.gray800,

        [darkThemeSelector]: {
          $$destructiveInitial: colors.gray300,
          $$destructiveStage: colors.gray50,
        },
      },
      positive: {
        $$destructiveInitial: colors.green500,
        $$destructiveStage: colors.green800,

        [darkThemeSelector]: {
          $$destructiveInitial: colors.green300,
          $$destructiveStage: colors.green50,
        },
      },
    },
  },
});
DestructiveDialogWarning.displayName = 'DestructiveDialogWarning';

export interface DestructiveDialogProps {
  heading?: string;
  actionLabel?: string;
  alert?: Omit<AlertProps, 'dismiss' | 'ref' | 'relation' | 'type'>;
  /**
   * If truthy, will require typing a confirmation value. If a string, uses that value, or if
   * boolean uses the `label` value.
   */
  confirmationStep?: boolean | string;
  irreversible?: boolean;
  content?: React.ReactNode;
  onDelete: MouseEventHandler<HTMLButtonElement>;
  onCancel?: MouseEventHandler<HTMLButtonElement>;
  icon?: IconName;
  label: React.ReactNode;
  state: OverlayTriggerState;
  preset?: 'narrow' | 'intermediate' | 'wide';
  variant?: ButtonVariantProp;
  loading?: boolean;
}

export function DestructiveDialog({
  heading,
  actionLabel = 'delete',
  alert,
  confirmationStep = false,
  irreversible = true,
  content,
  onDelete,
  onCancel,
  icon = 'trash-can',
  label,
  state,
  preset = 'narrow',
  variant = 'destructive',
  loading,
}: DestructiveDialogProps) {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [wasOpen, setWasOpen] = useState(state.isOpen);
  if (state.isOpen !== wasOpen) {
    setWasOpen(state.isOpen);
    if (!state.isOpen) {
      setInputValue('');
      setErrorMessage(undefined);
    }
  }

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const confirmationText = useMemo(
    () => (typeof confirmationStep === 'string' ? confirmationStep : label),
    [confirmationStep, label],
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (confirmationStep && inputValue !== confirmationText) {
        setErrorMessage('Must type confirmation prompt to confirm');
        return;
      }
      onDelete(event);
    },
    [confirmationStep, confirmationText, onDelete, inputValue],
  );

  const handleCancel = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onCancel?.(event);
      state.close();
    },
    [onCancel, state],
  );

  const submitRef = useRef<HTMLButtonElement>(null);

  const hasCopy = alert?.copy || irreversible;
  const hasAlert = alert?.heading || hasCopy;

  const alertCopy = hasCopy ? (
    <Text>
      {alert?.copy}
      {alert?.copy && irreversible && ' '}
      {irreversible && (
        <DestructiveDialogWarning variant={alert?.variant || 'negative'}>
          This action cannot be undone.
        </DestructiveDialogWarning>
      )}
    </Text>
  ) : null;

  return (
    <Dialog state={state} preset={preset}>
      <DialogHeader
        icon={icon}
        heading={
          heading ?? (
            <>
              {ucfirst(actionLabel)} {label}
            </>
          )
        }
      />
      <DialogContent gutter={{ edge: 'all' }}>
        {hasAlert && (
          <Alert
            variant={alert?.variant || 'negative'}
            icon={alert?.icon || 'warning'}
            {...alert}
            copy={alertCopy}
          />
        )}
        <Body>
          Are you sure you want to {actionLabel} <Text weight="bold">{label}</Text>?
        </Body>
        {content}
        {confirmationStep && (
          <FieldContainer>
            <PrimaryField
              label="Confirm"
              description={
                <Text>
                  Type <Text weight="bold">{confirmationText}</Text> to confirm.
                </Text>
              }
              element={
                <TextInput
                  value={inputValue}
                  onChange={(value) => handleInputChange(value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      // Submit on enter in input
                      submitRef.current?.click();
                    }
                  }}
                  invalid={!!errorMessage}
                />
              }
              errorMessage={errorMessage}
            />
          </FieldContainer>
        )}
      </DialogContent>
      <DialogFooter
        actions={
          <>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button ref={submitRef} onClick={handleDeleteClick} variant={variant} loading={loading}>
              {ucfirst(actionLabel)}
            </Button>
          </>
        }
      />
    </Dialog>
  );
}
